import { useEffect, useState, useCallback } from 'react';
import { appointmentSearchAdmin, IAppointment } from '../../../../API/GoMoto';
import { useAdminState } from '../../../../context/Admin/admin';
import { useLocker } from '../../../../context/hardware';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { ILockerFixture, LockerStatus } from '../../../../context/lockers';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { GMTable } from '../../../../controls/Table';
import { getFullDisplayName } from '../../../../helpers/Appointment';
import { KeyMapProps, LockerKey } from './KeyMap.types';
import { AppointmentLoanerOrRentalOption } from '../../../../API/Enums';
import { useLocalization } from '../../../../context/Localization/localization';
import { isDevelopment } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { LocalizationType } from '../../../../context/Localization/localization.types';
import { AppointmentDispatchTypes, useAppointmentDispatch } from '../../../../context/Appointment/appointment';
import { KeyMapRowButtons } from './KeyMapRowButtons/KeyMapRowButtons';
import { useAdminKeyDropProcess } from '../../../../context/AdminLockerOperation/adminLockerOperation';
import { KeyExchangeStage } from '../../../../context/Appointment/appointment.types';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';

/**
 * The Key Map component that allows for admin users to view what lockers are occupied and by who,
 * return keys, and open lockers.
 */
export const KeyMap: GoMotoComponent<KeyMapProps> = ({
    onProceed,
    setKeyUpdated,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { key: kioskKey, kioskTypeName } = useKioskState();
    const { lockers } = useLocker();
    const { LastLockerChanged, LoginSessionID } = useAdminState();
    const appointmentDispatch = useAppointmentDispatch();
    const [mzeroRetrieveKeyUpdated, setMzeroRetrieveKeyUpdated] = useState<boolean>(false);
    const { AdminKeyDropProcess, setLockerId, showKeyDropProcessDialog, onMzeroRetrieveKeyClick } = useAdminKeyDropProcess({
        onProceed: onProceed,
        setMzeroRetrieveComplete: setMzeroRetrieveKeyUpdated,
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [appointments, setAppointments] = useState<IAppointment[] | undefined>(undefined);
    const [lockerMap, setLockerMap] = useState<LockerKey[] | undefined>(undefined);

    const handleOnMzeroRetrieveKeyClick: GoMotoFunction<[IAppointment | undefined], Promise<void>> = useCallback(async (appt: IAppointment | undefined = undefined) => {
        if (onMzeroRetrieveKeyClick) {
            await onMzeroRetrieveKeyClick(appt);
            setMzeroRetrieveKeyUpdated(true);
        }
    }, [onMzeroRetrieveKeyClick]);

    const resetKeyMap: VoidFunction = useCallback(() => {
        appointmentDispatch({ type: AppointmentDispatchTypes.Remove });

        setAppointments(undefined);
        setLockerMap(undefined);
        setLoading(true);

        appointmentSearchAdmin({
            kioskKey,
            keySlotEmpty: false,
            sessionId: LoginSessionID,
        })
            .then(setAppointments)
            .then(() => setLoading(false));
    }, [LoginSessionID, appointmentDispatch, kioskKey]);

    useEffect(() => {
        if (mzeroRetrieveKeyUpdated) {
            resetKeyMap();
            setMzeroRetrieveKeyUpdated(false);
            setKeyUpdated(true);
        }
    }, [mzeroRetrieveKeyUpdated, resetKeyMap, setKeyUpdated]);

    useEffect(() => {
        resetKeyMap();
    }, [LastLockerChanged, resetKeyMap]);

    useEffect(() => {
        if (appointments && !lockerMap && lockers.length) {
            const updatedLockerMap: LockerKey[] = lockers.map((locker: ILockerFixture) => {
                let customerName: string = Localization.SlotNotAssigned;
                let lockerStatus: LockerStatus = locker.Status;
                let isKeyExchangeLockerOp: boolean = false;
                const appointmentOrLockerId: IAppointment | ILockerFixture['Id'] = appointments
                    .find((appointment: IAppointment) => appointment.keySlotNumber === locker.Id) ?? locker.Id;

                if (typeof appointmentOrLockerId === 'object') {
                    if (isDevelopment()) {
                        lockerStatus = 'Taken';
                    }

                    if (lockerStatus === 'Taken') {
                        isKeyExchangeLockerOp = appointmentOrLockerId.keyExchangeStage === KeyExchangeStage.AdminKeyRetrieve;

                        const customerDisplayName: string = getFullDisplayName(appointmentOrLockerId);

                        if (isKeyExchangeLockerOp) {
                            switch (appointmentOrLockerId.returningLoanerOrRental) {
                                case AppointmentLoanerOrRentalOption.Loaner:
                                    customerName = Localization.formatString(Localization.LoanerReturnCustomer, customerDisplayName) as string;
                                    break;
                                case AppointmentLoanerOrRentalOption.Rental:
                                    customerName = Localization.formatString(Localization.RentalReturnCustomer, customerDisplayName) as string;
                                    break;
                                default:
                                    customerName = customerDisplayName;
                                    break;
                            }
                        }
                        else {
                            customerName = customerDisplayName;
                        }
                    }
                }

                return {
                    slot: locker.Id,
                    customer: customerName,
                    status: lockerStatus === 'Available' ? Localization.NotOccupied : Localization.Occupied,
                    keyStatus: lockerStatus,
                    action: (
                        <KeyMapRowButtons
                            {...(appointmentOrLockerId !== undefined && typeof appointmentOrLockerId === 'object' ? {
                                lockerOpDataType: 'appointment',
                                appointment: appointmentOrLockerId,
                            } : {
                                lockerOpDataType: 'locker-id',
                                lockerId: appointmentOrLockerId,
                            })}
                            lockerStatus={lockerStatus}
                            showKeyDropProcessDialog={showKeyDropProcessDialog}
                            onMzeroRetrieveKeyClick={handleOnMzeroRetrieveKeyClick}
                            setLockerId={setLockerId}
                        />
                    ),
                };
            });

            setLockerMap(updatedLockerMap.sort((a, b) => a.slot - b.slot));
        }
    }, [Localization, appointmentDispatch, appointments, kioskTypeName, lockerMap, lockers, handleOnMzeroRetrieveKeyClick, setLockerId, showKeyDropProcessDialog]);

    if (!lockerMap || loading) {
        return <Spinner />;
    }
    else {
        return (
            <>
                <GMTable<4>
                    tableKey={'KeyMap'}
                    cellWidths={[10, 30, 20, 40]}
                    headers={[Localization.Slot, Localization.Customer, Localization.Status, Localization.Actions]}
                    rows={lockerMap.map((locker) => [locker.slot, locker.customer, locker.status, locker.action])}
                />
                <AdminKeyDropProcess />
            </>
        );
    }
};
